import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';

/**
 * Newspaper question data from the backend.
 */
interface NewspaperQuestion {
    id: number;
    question: string;
    answer: string | null;
    is_published: boolean;
    user: {
        id: number;
        name: string;
    } | null;
    answered_at: string | null;
    created_at: string;
}

/**
 * Props for the Edit page.
 */
interface Props {
    question: NewspaperQuestion;
}

/**
 * GM Newspaper Question Edit page - form to answer a question.
 */
export default function Edit({ question }: Props) {
    // Form fields
    const [answer, setAnswer] = useState(question.answer || '');
    const [isPublished, setIsPublished] = useState(question.is_published);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.patch(`/gm/newspaper/questions/${question.id}`, {
            answer,
            is_published: isPublished,
        }, {
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title="Answer Question" activePath="/gm/newspaper">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">❓ Answer Question</h1>
                    <Link href="/gm/newspaper/questions" className="text-gray-400 hover:text-white transition-colors">
                        ← Back to Questions
                    </Link>
                </div>

                {/* Question Display */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
                    <div className="text-sm text-gray-400 mb-2">
                        Asked by {question.user?.name || 'Unknown'} on{' '}
                        {new Date(question.created_at).toLocaleDateString()}
                    </div>
                    <p className="text-white text-lg font-medium">{question.question}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Answer Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Your Answer</h2>

                        {/* Answer - Markdown Editor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Answer <span className="text-red-400">*</span>
                            </label>
                            <div data-color-mode="dark">
                                <Editor
                                    value={answer}
                                    onChange={(value) => setAnswer(value)}
                                    placeholder="Write your answer here..."
                                    height={300}
                                />
                            </div>
                            {errors.answer && <p className="mt-1 text-sm text-red-400">{errors.answer}</p>}
                        </div>

                        {/* Published Status */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Publish answer (visible to all users)
                            </label>
                        </div>
                        {question.answered_at && (
                            <p className="text-xs text-gray-500">
                                First answered: {new Date(question.answered_at).toLocaleDateString()}
                            </p>
                        )}
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Answer'}
                        </button>
                        <Link
                            href="/gm/newspaper/questions"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}

